import {
	AddonProtected,
	BlocksPage,
	BrandPage,
	ExportPage,
	ExportProvider,
	FooterLinksPage,
	HasPermission,
	NotFound,
	RolePage,
	SidebarLayout,
	useDynamicConfig,
} from "@dgs/core";
import { ErrorBoundary } from "@sentry/react";
import { FC } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router";
import { Addon } from "../../shared/types/addon";
import { AccessTokenPage } from "./accessTokens/AccessTokenPage";
import { AnalyticsPage } from "./analytics/AnalyticsPage";
import { ApplicationPage } from "./applications/ApplicationPage";
import { AppNotificationPage } from "./appNotifications/AppNotificationPage";
import { AppSettingsPage } from "./appSettings/AppSettingsPage";
import { BottomNavBarItemsPage } from "./bottomNavBarItems/BottomNavBarItemsPage";
import { CampaignPage } from "./campaigns";
import { CategoryPage } from "./categories";
import { ClippingPage } from "./clippings";
import { CompanyPage } from "./companies";
import { config } from "./config";
import { ConfigurationsPage } from "./configurations/ConfigurationsPage";
import { ConsentPage } from "./consents";
import { ContactDatabaseSettingsPage } from "./contactDatabaseSettings/ContactDatabaseSettingsPage";
import { ContactFilterPage } from "./contactFilter";
import { ContactPage } from "./contacts";
import { ContactTypePage } from "./contactType/ContactTypePage";
import { ContactUpdateInquiryPage } from "./contactUpdateInquiries/ContactUpdateInquiryPage";
import { ContactViewList } from "./contactViews";
import { ContentManagementPage } from "./contentManagement/ContentManagementPage";
import { ContentProviders } from "./ContentProviders";
import { CostCategoriesPage } from "./costCategories";
import { DashboardPage } from "./dashboard/DashboardPage";
import { DataFieldList } from "./datafields";
import { DeletionRequestPage } from "./deletionRequests/DeletionRequestPage";
import { DistributionListPage } from "./distributionLists";
import { EventCategoryPage } from "./eventCategories";
import { AdminEventsPage } from "./events/AdminEventsPage";
import { EventTypesPage } from "./eventTypes/EventTypesPage";
import { AdminFeedbackPage } from "./feedbacks/AdminFeedbackPage";
import { FilesPage } from "./FilesPage";
import { Header } from "./header";
import { IndustryPage } from "./Industry";
import { IntegrationPage } from "./integrations/IntegrationPage";
import { LocationPage } from "./locations";
import { MenuPage } from "./menu/MenuPage";
import { OrganizationalUnitPage } from "./organizationalUnits";
import { OrganizersPage } from "./organizers";
import { PreferencePage } from "./preferences";
import { ProductTranslationsPage } from "./productTranslations";
import { ProfileSettingsPage } from "./profileSettings/ProfileSettingsPage";
import { RegionPage } from "./region/RegionPage";
import { userService } from "./shared/api/userService";
import { DefaultContentLayout } from "./shared/Layout";
import { ErrorPage } from "./shared/ui/ErrorPage";
import { Sidebar } from "./sidebar";
import { TagPage } from "./tags";
import { TargetGroupPage } from "./targetGroups";
import { TenantsPage } from "./tenants";
import { UserGroupsPage } from "./userGroups";
import { UserOnboardingPage } from "./userOnboarding/UserOnboardingPage";
import { UserPreferencesPage } from "./userPreferences/UserPreferencesPage";
import { UserResponsibilityPage } from "./userResponsibilities/UserResponsibilityPage";
import { UsersPage } from "./users";

export const Content: FC = () => {
	const { apiUrl } = useDynamicConfig();

	return (
		<ContentProviders>
			<SidebarLayout
				sideBar={<Sidebar />}
				header={<Header />}
				content={
					<ErrorBoundary fallback={<ErrorPage />}>
						<Routes>
							<Route
								element={
									<DefaultContentLayout>
										<Outlet />
									</DefaultContentLayout>
								}
							>
								<Route
									path=""
									element={
										<HasPermission permissions={["contact.read"]} fallback={<Navigate to="/events" />}>
											<Navigate replace={true} to="/dashboard" />
										</HasPermission>
									}
								/>
								<Route path="brand" element={<BrandPage frontendThemeName={config.frontendThemeName} />} />
								<Route path="files/*" element={<FilesPage />} />
								<Route path="events">
									<Route path="*" index element={<AdminEventsPage />} />
								</Route>
								<Route path="applications">
									<Route path="*" index element={<ApplicationPage />} />
								</Route>
								<Route path="users">
									<Route path="*" index element={<UsersPage />} />
								</Route>
								<Route path="roles">
									<Route path="*" index element={<RolePage queriesToInvalidate={[userService.indexKey]} />} />
								</Route>
								<Route path="feedbacks">
									<Route path="*" index element={<AdminFeedbackPage />} />
								</Route>
								<Route path="consents" element={<ConsentPage />} />
								<Route path="distribution-lists">
									<Route
										path="*"
										index
										element={
											<AddonProtected addon={Addon.CONTACT_DATABASE}>
												<DistributionListPage />
											</AddonProtected>
										}
									/>
								</Route>
								<Route path="tenants" element={<TenantsPage />} />
								<Route path="data-fields">
									<Route
										path="*"
										index
										element={
											<AddonProtected addon={Addon.CONTACT_DATABASE}>
												<DataFieldList />
											</AddonProtected>
										}
									/>
								</Route>
								<Route path="preferences">
									<Route
										path="*"
										index
										element={
											<AddonProtected addon={Addon.PREFERENCES}>
												<PreferencePage />
											</AddonProtected>
										}
									/>
								</Route>
								<Route path="categories">
									<Route path="*" index element={<CategoryPage />} />
								</Route>
								<Route path="tags">
									<Route path="*" index element={<TagPage />} />
								</Route>
								<Route path="access-tokens">
									<Route path="*" index element={<AccessTokenPage />} />
								</Route>
								<Route path="integrations">
									<Route path="*" index element={<IntegrationPage />} />
								</Route>
								<Route
									path="contact-update-campaigns/*"
									element={
										<AddonProtected addon={Addon.CONTACT_DATABASE}>
											<ContactUpdateInquiryPage />
										</AddonProtected>
									}
								/>
								<Route path="contact-views">
									<Route
										path="*"
										index
										element={
											<AddonProtected addon={Addon.CONTACT_DATABASE}>
												<ContactViewList />
											</AddonProtected>
										}
									/>
								</Route>
								<Route path="deletion-requests">
									<Route path="*" index element={<DeletionRequestPage />} />
								</Route>
								<Route path="contact-filters">
									<Route
										path="*"
										index
										element={
											<AddonProtected addon={Addon.CONTACT_DATABASE}>
												<ContactFilterPage />
											</AddonProtected>
										}
									/>
								</Route>
								<Route path="app">
									<Route
										path="notifications/*"
										element={
											<AddonProtected addon={Addon.APP}>
												<AppNotificationPage />
											</AddonProtected>
										}
									/>
									<Route
										path="nav-bar"
										element={
											<AddonProtected addon={Addon.APP}>
												<BottomNavBarItemsPage />
											</AddonProtected>
										}
									/>
									<Route
										path="settings"
										element={
											<AddonProtected addon={Addon.APP}>
												<AppSettingsPage />
											</AddonProtected>
										}
									/>
									<Route path="*" element={<NotFound />} />
								</Route>
								<Route path="contact-settings">
									<Route
										path="*"
										index
										element={
											<AddonProtected addon={Addon.CONTACT_DATABASE}>
												<ContactDatabaseSettingsPage />
											</AddonProtected>
										}
									/>
								</Route>
								<Route path="clippings">
									<Route
										path="*"
										index
										element={
											<AddonProtected addon={Addon.CONTACT_DATABASE}>
												<ClippingPage />
											</AddonProtected>
										}
									/>
								</Route>
								<Route path="user-groups">
									<Route path="*" index element={<UserGroupsPage />} />
								</Route>
								<Route path="footer-links">
									<Route path="*" index element={<FooterLinksPage />} />
								</Route>
								<Route path="blocks">
									<Route
										path="*"
										element={
											<AddonProtected addon={Addon.CMS}>
												<BlocksPage />
											</AddonProtected>
										}
									/>
								</Route>
								<Route path="event-settings">
									<Route
										path="event-types/*"
										index
										element={
											<HasPermission permissions={["eventTypes.read"]}>
												<EventTypesPage />
											</HasPermission>
										}
									/>
									<Route
										path="organizational-units/*"
										element={
											<HasPermission permissions={["organizationalUnits.read"]}>
												<OrganizationalUnitPage />
											</HasPermission>
										}
									/>
									<Route
										path="target-groups/*"
										element={
											<HasPermission permissions={["targetGroup.read"]}>
												<TargetGroupPage />
											</HasPermission>
										}
									/>
									<Route
										path="event-categories/*"
										element={
											<HasPermission permissions={["eventCategory.read"]}>
												<EventCategoryPage />
											</HasPermission>
										}
									/>
									<Route
										path="regions/*"
										element={
											<HasPermission permissions={["region.read"]}>
												<RegionPage />
											</HasPermission>
										}
									/>
									<Route
										path="locations/*"
										element={
											<HasPermission permissions={["location.read"]}>
												<LocationPage />
											</HasPermission>
										}
									/>
									<Route
										path="industries/*"
										element={
											<HasPermission permissions={["industry.read"]}>
												<IndustryPage />
											</HasPermission>
										}
									/>
									<Route
										path="organizers/*"
										element={
											<HasPermission permissions={["organizers.read"]}>
												<OrganizersPage />
											</HasPermission>
										}
									/>
									<Route
										path="cost-categories/*"
										element={
											<HasPermission permissions={["costCategory.read"]}>
												<CostCategoriesPage />
											</HasPermission>
										}
									/>
									<Route
										path="user-responsibilities/*"
										element={
											<HasPermission permissions={["userResponsibility.read"]}>
												<UserResponsibilityPage />
											</HasPermission>
										}
									/>
								</Route>
								<Route
									path="content-management/*"
									element={
										<AddonProtected addon={Addon.CMS}>
											<ContentManagementPage />
										</AddonProtected>
									}
								/>
								<Route path="menu">
									<Route path="*" index element={<MenuPage />} />
								</Route>
							</Route>
							<Route path="contacts">
								<Route
									path="*"
									index
									element={
										<AddonProtected addon={Addon.CONTACT_DATABASE}>
											<ContactPage />
										</AddonProtected>
									}
								/>
							</Route>
							<Route path="contact-types">
								<Route
									path="*"
									index
									element={
										<DefaultContentLayout>
											<AddonProtected addon={Addon.CONTACT_DATABASE}>
												<ContactTypePage />
											</AddonProtected>
										</DefaultContentLayout>
									}
								/>
							</Route>
							<Route path="companies">
								<Route
									index
									path="*"
									element={
										<AddonProtected addon={Addon.CONTACT_DATABASE}>
											<CompanyPage />
										</AddonProtected>
									}
								/>
							</Route>
							<Route
								path="exports"
								element={
									<DefaultContentLayout>
										<ExportProvider>
											<ExportPage apiUrl={apiUrl} />
										</ExportProvider>
									</DefaultContentLayout>
								}
							/>
							<Route path="campaigns">
								<Route path="*" index element={<CampaignPage />} />
							</Route>
							<Route path="dashboard">
								<Route path="*" index element={<DashboardPage />} />
							</Route>
							<Route path="user-preferences">
								<Route path="*" index element={<UserPreferencesPage />} />
							</Route>
							<Route path="user-onboarding">
								<Route path="*" index element={<UserOnboardingPage />} />
							</Route>
							<Route path="analytics">
								<Route path="*" index element={<AnalyticsPage />} />
							</Route>
							<Route path="settings">
								<Route path="*" index element={<ConfigurationsPage />} />
							</Route>
							<Route path="product-translations" element={<ProductTranslationsPage />} />
							<Route path="profile-settings" element={<ProfileSettingsPage />} />
							<Route path="*" element={<NotFound />} />
						</Routes>
					</ErrorBoundary>
				}
			/>
		</ContentProviders>
	);
};
